import { FC, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  calculateInvoiceTaskTotals,
  calculateInvoiceTotals,
} from '@shinetools/invoicing-rules';

import { TransactionFeeType } from '__generated__/GQL';
import { getFeesForType } from 'common/fees';
import Callout from 'components/_core/Callout';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import { InvoiceFormSchema } from 'features/Work/form/invoiceForm.schema';

import { useInvoiceFormContext } from '../../../../../context';
import { PaymentDescriptionList } from '../PaymentDescriptionList';
import {
  SunshineCardHeader,
  SunshineCardWithHeader,
} from '../SunshineCardWithHeader';
import locales from './locales';

const descriptionItems = [
  locales.formatString(
    locales.creditCardFeesInsideEU,
    getFeesForType(TransactionFeeType.CardPayinEuro),
  ),
  locales.formatString(
    locales.creditCardFeesOutsideEU,
    getFeesForType(TransactionFeeType.CardPayinNonEuro),
  ),
];

export const CreditCardPayment: FC = () => {
  const {
    metadata: { maxAmountForCardPayment },
  } = useInvoiceFormContext();

  const { control, setValue, watch } = useFormContext<InvoiceFormSchema>();

  const allowCreditCardPayment = watch('allowCreditCardPayment');
  const tasks = watch('tasks');

  /**
   * We need to re-compute here totals for the `canPayWithCard`
   * @todo improve the compute totals logic by doing it once on the parent component
   * @see https://linear.app/shine/issue/CAD-859
   */
  const formattedTasks = tasks.map((task) => ({
    ...task,
    ...calculateInvoiceTaskTotals(task),
  }));

  const { totalAmountAfterTaxes } = calculateInvoiceTotals(formattedTasks);

  const canPayWithCard = totalAmountAfterTaxes < maxAmountForCardPayment;

  useEffect(() => {
    const newAllowCreditCardPayment = canPayWithCard && allowCreditCardPayment;

    setValue('allowCreditCardPayment', newAllowCreditCardPayment);
  }, [allowCreditCardPayment, canPayWithCard, setValue]);

  const isCreditCardPaymentPossible = maxAmountForCardPayment !== 0;

  if (!isCreditCardPaymentPossible) {
    return null;
  }

  return canPayWithCard ? (
    <>
      <Callout marginBottom="space-8">
        <Callout.Title>{locales.calloutTitle}</Callout.Title>
        <Callout.Content>{locales.calloutContent}</Callout.Content>
      </Callout>
      <Controller
        control={control}
        name="allowCreditCardPayment"
        render={({ field }) => (
          <SunshineCardWithHeader
            isChecked={field.value}
            onChange={field.onChange}
            variant="checkbox"
          >
            <SunshineCardHeader>
              <SunshineIcon marginRight="space-8" name="flash" />
              <Text as="span" fontSize="font-12">
                {locales.creditCardHighlight}
              </Text>
            </SunshineCardHeader>

            <SunshineCard.Content>
              <SunshineCard.Title>{locales.creditCard}</SunshineCard.Title>

              <SunshineCard.Text>
                <PaymentDescriptionList items={descriptionItems} />
              </SunshineCard.Text>
            </SunshineCard.Content>
          </SunshineCardWithHeader>
        )}
      />
    </>
  ) : (
    <SunshineCard isChecked={false} isDisabled variant="checkbox">
      <SunshineCard.Content>
        <SunshineCard.Title>{locales.creditCard}</SunshineCard.Title>

        <SunshineCard.Text>
          {locales.formatString(
            locales.creditCardNotAllowed,
            maxAmountForCardPayment,
          )}
        </SunshineCard.Text>
      </SunshineCard.Content>
    </SunshineCard>
  );
};
